import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import * as React from 'react';
import { toast } from 'sonner';
import { EventLogger, IEventArgs } from '../extViewmodels';

// The Bugsnag logger only reports an event whose action contains "Error"; anything else is just a breadcrumb.
type QueryErrorLogging = Omit<IEventArgs, 'action'> & { action: `${string}-Error` };

// A type alias, not an interface: React Query only accepts a registered queryMeta assignable to Record<string, unknown>.
export type LevStandardQueryMeta = {
	toastErrorMessage?: string | ((error: Error) => string);
	errorLogging?: QueryErrorLogging | ((error: Error) => QueryErrorLogging | undefined);
};

declare module '@tanstack/react-query' {
	interface Register {
		queryMeta: LevStandardQueryMeta;
	}
}

export const reactQueryClient = new QueryClient({
	queryCache: new QueryCache({
		onError: (error, query) => {
			if (!query.meta) {
				return;
			}
			const meta = query.meta;
			if (meta.toastErrorMessage) {
				const message =
					typeof meta.toastErrorMessage === 'function' ? meta.toastErrorMessage(error) : meta.toastErrorMessage;
				// A function-form message may return an empty string to suppress the toast for
				// errors the UI handles inline (e.g. an expected 404 rendered as an empty state).
				if (message) {
					toast.error(message);
				}
			}
			// A function form may return undefined to skip logging errors the UI expects (e.g. a 404 shown as not-found).
			const errorLogging = typeof meta.errorLogging === 'function' ? meta.errorLogging(error) : meta.errorLogging;
			if (errorLogging) {
				const { systemMessage, systemCode } = error as { systemMessage?: string; systemCode?: number };
				EventLogger.logEvent(errorLogging, { error, systemMessage, systemCode });
			}
		},
	}),
});
export function ReactQueryProvider({ children }: { children: React.ReactNode }) {
	return <QueryClientProvider client={reactQueryClient}>{children}</QueryClientProvider>;
}
